import { defineStore } from 'pinia';
import axios from 'axios';
import { useNotificationStore } from '@/store/global/notification';
import { useUrlsStore } from '@/store/visibility/topUrls';
import { decompressJson } from '@/utils/unzip';

export const useKeywordsStore = defineStore('Keywords', {
	state: () => ({
		remaining_call_number: 0,
		raw_keywords: [],
		raw_keywords_v2: [],
		count_raw_keywords: 0,

		keywords: [],
		keywords_v2: [],
		count_keywords: 0,

		labels: [],
		labelsOptional: [],
		deletedLabels: [],
		selectedColumnsFirstLoad: true,
		average_position_range: [],

		filterString: { value: '', type: 'global', id: null },
		filterByTop: 0,
		showBranding: true,
		searchedKeywords: [],
		searchedKeywordsSite: [],
		searchedKeywordsPage: [],
		relatedSearch: [],
		pplAlsoAsk: [],
		pplAlsoSearch: [],
		apiResponseStatus: null,
		searchedKeywordsLoading: false,
		querySearchText: '',
		error_message: null,
		log_error_id: null,

		volume_percentile: [],

		loading: true,
		loadingAnimation: false,

		num_submitted: 0,
		num_unique: 0,
		num_existent: 0,
		num_duplicates: 0,
		num_added: 0,
		num_updated: 0,
		added_keywords: [],
		updated_keywords: [],
		duplicates: [],
		existent_keywords: [],
		existent_keywords_data: [],
		existent_labels: [],
		selectedMarketOptional: 0,
		marketsOptional: [],
		label_trend: [],
		keyword_trend: [],
		selectedMarket: 0,
		totalKeywords: 0,
		maxKeywords: 0,
		raw_competitors_list: [],
		competitors_list: [],
		selectedCompetitorsLabel: null,
		lastFetchedMarketId: null,
		fetchCompetitorsPromise: null,
	}),
	getters: {
		kpi_stats(state) {
			const stats = {
				top_3: 0,
				last_top_3: 0,
				top_5: 0,
				last_top_5: 0,
				top_10: 0,
				last_top_10: 0,
				total_position: 0,
				count_position: 0,
				last_total_position: 0,
				last_count_position: 0,
				total_keywords: state.keywords_v2.length,
			};

			state.keywords_v2.forEach(keyword => {
				// Current Positions
				if (keyword.client_position !== null && keyword.client_position !== 101) {
					if (keyword.client_position <= 3) stats.top_3++;
					if (keyword.client_position <= 5) stats.top_5++;
					if (keyword.client_position <= 10) stats.top_10++;

					stats.total_position += keyword.client_position;
					stats.count_position++;
				}

				// Last Positions
				if (keyword.old_client_position !== null && keyword.old_client_position !== 101) {
					if (keyword.old_client_position <= 3) stats.last_top_3++;
					if (keyword.old_client_position <= 5) stats.last_top_5++;
					if (keyword.old_client_position <= 10) stats.last_top_10++;

					stats.last_total_position += keyword.old_client_position;
					stats.last_count_position++;
				}
			});

			return stats;
		},

		top_3(state) {
			return this.kpi_stats.top_3;
		},
		percentage_top_3(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.top_3 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		last_top_3(state) {
			return this.kpi_stats.last_top_3;
		},
		last_percentage_top_3(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.last_top_3 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		is_top_3_bigger(getters) {
			if (getters.top_3 > getters.last_top_3) {
				return true;
			} else if (getters.top_3 == getters.last_top_3) {
				return true;
			} else {
				return false;
			}
		},
		evolution_top_3(getters) {
			if (getters.last_top_3 === 0) {
				if (getters.top_3 === 0) {
					return '0 - 0 | 0%';
				} else {
					return '0 - ' + getters.top_3 + ' | Infinity%';
				}
			}

			const percentageChange = ((getters.top_3 - getters.last_top_3) / getters.last_top_3) * 100;
			return getters.last_top_3 + ' - ' + getters.top_3 + ' | ' + percentageChange.toFixed(0) + '%';
		},

		top_5(state) {
			return this.kpi_stats.top_5;
		},
		percentage_top_5(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.top_5 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		last_top_5(state) {
			return this.kpi_stats.last_top_5;
		},
		last_percentage_top_5(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.last_top_5 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		is_top_5_bigger(getters) {
			if (getters.top_5 > getters.last_top_5) {
				return true;
			} else if (getters.top_5 == getters.last_top_5) {
				return true;
			} else {
				return false;
			}
		},
		evolution_top_5(getters) {
			if (getters.last_top_5 === 0) {
				if (getters.top_5 === 0) {
					return '0 - 0 | 0%';
				} else {
					return '0 - ' + getters.top_5 + ' | Infinity%';
				}
			}

			const percentageChange = ((getters.top_5 - getters.last_top_5) / getters.last_top_5) * 100;
			return getters.last_top_5 + ' - ' + getters.top_5 + ' | ' + percentageChange.toFixed(0) + '%';
		},

		top_10(state) {
			return this.kpi_stats.top_10;
		},
		percentage_top_10(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.top_10 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		last_top_10(state) {
			return this.kpi_stats.last_top_10;
		},
		last_percentage_top_10(state) {
			if (state.keywords_v2.length === 0) return '0%';
			return ((this.kpi_stats.last_top_10 / state.keywords_v2.length) * 100).toFixed(0) + '%';
		},
		is_top_10_bigger(getters) {
			if (getters.top_10 > getters.last_top_10) {
				return true;
			} else if (getters.top_10 == getters.last_top_10) {
				return true;
			} else {
				return false;
			}
		},
		evolution_top_10(getters) {
			if (getters.last_top_10 === 0) {
				if (getters.top_10 === 0) {
					return '0 - 0 | 0%';
				} else {
					return '0 - ' + getters.top_10 + ' | Infinity%';
				}
			}

			const percentageChange = ((getters.top_10 - getters.last_top_10) / getters.last_top_10) * 100;
			return getters.last_top_10 + ' - ' + getters.top_10 + ' | ' + percentageChange.toFixed(0) + '%';
		},

		average_position(state) {
			if (this.kpi_stats.count_position === 0) return 0;
			return parseInt((this.kpi_stats.total_position / this.kpi_stats.count_position).toFixed(0));
		},

		last_average_position(state) {
			if (this.kpi_stats.last_count_position === 0) return 0;
			return parseInt((this.kpi_stats.last_total_position / this.kpi_stats.last_count_position).toFixed(0));
		},

		is_average_position_bigger(getters) {
			return getters.average_position > getters.last_average_position;
		},
		evolution_average_position(getters) {
			if (getters.last_average_position === 0) {
				if (getters.average_position === 0) {
					return '0 - 0 | 0%';
				} else {
					return '0 - ' + getters.average_position + ' | Infinity%';
				}
			}

			const percentageChange = ((getters.average_position - getters.last_average_position) / getters.last_average_position) * 100;

			const average_evolution_percentage = percentageChange >= 0 ? percentageChange.toFixed(0) : (percentageChange * -1).toFixed(0);

			return getters.last_average_position + ' - ' + getters.average_position + ' | ' + average_evolution_percentage + '%';
		},
		getSearchLoaded(state) {
			return state.searchedKeywordsLoaded;
		},
		marketNameOptional(state) {
			const market = state.marketsOptional.find(m => m.id === state.selectedMarketOptional);
			return market ? market.name : null;
		},
		joinedTop3(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position > 3 && keyword.client_position <= 3 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		leftTop3(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position <= 3 && keyword.client_position > 3 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		summaryJoinedTop3(state) {
			const joined = this.joinedTop3;
			return {
				averagePosition: this._averagePosition(joined),
				sumVolume: this._sumVolume(joined),
				count: joined.length,
			};
		},
		summaryLeftTop3(state) {
			const left = this.leftTop3;
			return {
				averagePosition: this._averagePosition(left),
				sumVolume: this._sumVolume(left),
				count: left.length,
			};
		},
		joinedTop5(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position > 5 && keyword.client_position <= 5 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		leftTop5(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position <= 5 && keyword.client_position > 5 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		summaryJoinedTop5(state) {
			const joined = this.joinedTop5;
			return {
				averagePosition: this._averagePosition(joined),
				sumVolume: this._sumVolume(joined),
				count: joined.length,
			};
		},
		summaryLeftTop5(state) {
			const left = this.leftTop5;
			return {
				averagePosition: this._averagePosition(left),
				sumVolume: this._sumVolume(left),
				count: left.length,
			};
		},
		joinedTop10(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position > 10 && keyword.client_position <= 10 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		leftTop10(state) {
			const keywords = state.keywords_v2.filter(keyword => keyword.old_client_position <= 10 && keyword.client_position > 10 && keyword.client_position != 101);
			const uniqueKeywords = new Map();
			keywords.forEach(kw => {
				const key = kw.keyword.toLowerCase();
				if (!uniqueKeywords.has(key) || kw.client_position < uniqueKeywords.get(key).client_position) {
					const { keyword: _, ...rest } = kw;
					uniqueKeywords.set(key, { ...rest, keyword: kw.keyword });
				}
			});
			return Array.from(uniqueKeywords.values());
		},
		summaryJoinedTop10(state) {
			const joined = this.joinedTop10;
			return {
				averagePosition: this._averagePosition(joined),
				sumVolume: this._sumVolume(joined),
				count: joined.length,
			};
		},
		summaryLeftTop10(state) {
			const left = this.leftTop10;
			return {
				averagePosition: this._averagePosition(left),
				sumVolume: this._sumVolume(left),
				count: left.length,
			};
		},
	},
	actions: {
		async fetchProjectsMarkets(project_id) {
			const response = await axios.post(
				'vuejs/get_projects_markets',
				{
					project_id: project_id,
				},
				{ withCredentials: true },
			);

			this.marketsOptional = response.data.data.markets;

			if ((this.selectedMarketOptional === null && this.marketsOptional.length > 0) || this.marketsOptional.findIndex(elem => elem.id === this.selectedMarketOptional) === -1) {
				this.selectedMarketOptional = this.marketsOptional[0].id;
			}

			return response.data.data;
		},

		async fetchOptionalLabels(marketId) {
			const response = await axios.post(
				'vuejs/get_labels',
				{
					market_id: marketId,
				},
				{ withCredentials: true },
			);

			this.labelsOptional = response.data.labels;

			return response.data.labels;
		},

		async importLabels(marketId, sourceMarketId, labels) {
			const response = await axios.post(
				'vuejs/import_labels',
				{
					market_id: marketId,
					source_market_id: sourceMarketId,
					labels: labels,
				},
				{ withCredentials: true },
			);

			return response.data;
		},


		// async fetchData(marketId, loadAnimation = true, labelId = null) {
		// 	if (loadAnimation) {
		// 		this.loading = true;
		// 	}
		// 	else {
		// 		this.loading = false;
		// 	}

		// 	this.selectedMarket = marketId;

		// 	const selected_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));

		// 	let startDate, endDate, date_from, date_to;

		// 	if (selected_dates && selected_dates.from && selected_dates.to) {
		// 		// TODO: change this because if visibilitySelectedDates is not set, it will not work and make error in the console with selected_dates.from
		// 		const selected_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));

		// 		date_from = selected_dates.from;
		// 		date_to = selected_dates.to;
		// 	}
		// 	else {
		// 		endDate = new Date();
		// 		startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

		// 		date_from = startDate.toISOString().substring(0, 10).split('-');
		// 		date_to = endDate.toISOString().substring(0, 10).split('-');

		// 		date_from = date_from[2] + '-' + date_from[1] + '-' + date_from[0];
		// 		date_to = date_to[2] + '-' + date_to[1] + '-' + date_to[0];
		// 	}

		// 	this.raw_keywords = [];

		// 	const response = await axios.post(
		// 		'vuejs/get_keywords',
		// 		{
		// 			market_id: marketId,
		// 			date_range_start: date_from,
		// 			date_range_end: date_to,
		// 		},
		// 		{ withCredentials: true },
		// 	);

		// 	// this.fetchAveragePositionRange(marketId)
		// 	this.fetchLabels(marketId);
		// 	this.fetchVolumePercentile(marketId);

		// 	if (response['data']['data']) {
		// 		this.raw_keywords = response.data.data.map(keyword => ({
		// 			...keyword,
		// 			checked: false,
		// 		}));
		// 		this.count_raw_keywords = this.raw_keywords.length;
		// 	}

		// 	this.keywords = this.raw_keywords;
		// 	this.count_keywords = this.count_raw_keywords;

		// 	this.original_top_3 = this.raw_keywords.filter(keyword => keyword.client.position <= 3 && keyword.client.position !== null && keyword.client.position !== 101).length;
		// 	this.original_top_5 = this.raw_keywords.filter(keyword => keyword.client.position <= 5 && keyword.client.position !== null && keyword.client.position !== 101).length;
		// 	this.original_top_10 = this.raw_keywords.filter(keyword => keyword.client.position <= 10 && keyword.client.position !== null && keyword.client.position !== 101).length;

		// 	const filteredKeywords = this.raw_keywords.filter(keyword => keyword.client.position !== null && keyword.client.position !== 101);
		// 	if (filteredKeywords.length > 0) {
		// 		this.original_average_position = parseInt((filteredKeywords.reduce((acc, keyword) => acc + keyword.client.position, 0) / filteredKeywords.length).toFixed(0));
		// 	}

		// 	this.filterString = { value: '', type: 'global' };
		// 	this.filterByTop = 0;
		//     this.loading = false;
		// 	return response.data.data;
		// },

		async fetchDataV2(marketId, loadAnimation = true, labelId = this.selectedCompetitorsLabel) {
			if (loadAnimation) {
				this.loading = true;
			}
			else {
				this.loading = false;
			}

			const selected_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));
			let startDate, endDate, date_from, date_to;

			if (selected_dates && selected_dates.from && selected_dates.to) {
				date_from = selected_dates.from;
				date_to = selected_dates.to;
			}
			else {
				endDate = new Date();
				startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

				date_from = startDate.toISOString().substring(0, 10).split('-');
				date_to = endDate.toISOString().substring(0, 10).split('-');

				date_from = date_from[2] + '-' + date_from[1] + '-' + date_from[0];
				date_to = date_to[2] + '-' + date_to[1] + '-' + date_to[0];
			}

			try {
				const response_v2 = await axios.post(
					'vuejs/get_keywords_v2',
					{
						market_id: marketId,
						label_id: labelId,
						date_range_start: date_from,
						date_range_end: date_to,
					},
					{ withCredentials: true },
				);

				const data = Array.isArray(response_v2?.data?.data) ? response_v2.data.data : [];
				for (let i = 0; i < data.length; i++) {
					const keyword = data[i];
					keyword.checked = false;

					const labels = keyword.labels;
					if (typeof labels === 'string') {
						try {
							keyword.labels = JSON.parse(labels);
						} catch (_) {
							keyword.labels = [];
						}
					} else if (Array.isArray(labels)) {
						keyword.labels = labels;
					} else {
						keyword.labels = [];
					}

					keyword.creation_date_object = new Date(keyword.creation_date);

					if (Array.isArray(keyword.competitors)) {
						for (let j = 0; j < keyword.competitors.length; j++) {
							const competitor = keyword.competitors[j];
							if (competitor && competitor.order != null) {
								keyword[`competitor${competitor.order}`] = competitor.competitor_position;
							}
						}
					}
				}

				this.raw_keywords_v2 = data;
				this.keywords_v2 = data;

				this.raw_keywords = data;
				this.keywords = data;

				void this.fetchLabels(marketId);
				void this.fetchVolumePercentile(marketId);

				return data;
			} catch (error) {
				console.error('Error fetching keywords_v2:', error);
				return [];
			} finally {
				this.loading = false;
			}
		},

		async fetchAveragePositionRange(marketId, competitorId = null) {
			const selected_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));

			const response = await axios.post(
				'vuejs/get_average_keywords',
				{
					market_id: marketId,
					date_range_start: selected_dates.from,
					date_range_end: selected_dates.to,
					competitor_label_id: competitorId,
				},
				{ withCredentials: true },
			);

			this.average_position_range = response.data.data;
			return response.data.data;
		},

		async fetchLabels(marketId) {
			const response = await axios.post(
				'vuejs/get_labels',
				{
					market_id: marketId,
				},
				{ withCredentials: true },
			);

			this.labels = response.data.labels;
			this.deletedLabels = response.data.deleted_labels;

			return response.data.data;
		},

		async createLabelIfNotExists(marketId, labelColor, labelName, labelDescription) {
			const response = await axios.post(
				'vuejs/create_label_if_not_exists',
				{
					market_id: marketId,
					label_color: labelColor,
					label_name: labelName,
					label_description: labelDescription,
				},
				{ withCredentials: true },
			);

			this.labels.push({
				id: response.data.data,
				label: labelName,
				description: labelDescription,
				color: labelColor,
			});

			return {
				market_id: marketId,
				label_color: labelColor,
				label_name: labelName,
				label_description: labelDescription,
				id: response.data.data,
			};
		},

		async fetchVolumePercentile(marketId) {
			const response = await axios.post(
				'vuejs/get_volume_percentile',
				{
					market_id: marketId,
				},
				{ withCredentials: true },
			);

			this.volume_percentile = response.data.data;

			return response.data.data;
		},

		async createKeywords(marketId, keywords, labels) {
			let response;

			try {
				response = await axios.post(
					'vuejs/create_keyword',
					{
						market_id: marketId,
						keywords: keywords,
						labels: labels,
					},
					{ withCredentials: true },
				);
			} catch (error) {
				const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
				console.error('An error occurred:', errorMessage);
				useNotificationStore().open('error', `Error: ${errorMessage}`);
				return;
			}

			if (response.status == 200) {
				let newKeywords = {
					avg_words: null,
					client: {
						last_position: 101,
						position: 101,
					},
					client_full_url: null,
					cmp: null,
					cmp_seo: null,
					competitor1: {
						last_position: 101,
						position: 101,
						name: null,
					},
					competitor1_full_url: 'None',
					competitor2: {
						last_position: 101,
						position: 101,
						name: null,
					},
					competitor2_full_url: 'None',
					competitor3: {
						last_position: 101,
						position: 101,
						name: null,
					},
					competitor3_full_url: 'None',
					competitor4: {
						last_position: 101,
						position: 101,
						name: null,
					},
					competitor4_full_url: 'None',
					competitor5: {
						last_position: 101,
						position: 101,
						name: null,
					},
					competitor5_full_url: 'None',
					creation_date: null,
					id: response.data.data[0].id,
					keyword: response.data.data[0].keyword,
					labels: [],
					se_results: null,
					seo_potential: 101,
					volume: null,
				};

				labels.forEach(label_id => {
					let label = this.labels.find(label => label.id === label_id);
					if (label) {
						newKeywords.labels.push(label);
					}
				});

				useNotificationStore().open('success', 'keywords created successfully');
				this.raw_keywords.push(newKeywords);
				this.keywords = this.raw_keywords;
				this.count_raw_keywords = this.raw_keywords.length;
				this.count_keywords = this.keywords.length;
				this.fetchDataV2(marketId, false)
			}
			else {
				let errorMessage = response.request.responseText ? JSON.parse(response.request.responseText).message : response.request.statusText;
				useNotificationStore().open('error', response.request.status + ': ' + errorMessage);
			}

			return response.data.data;
		},

		updateSelectedColumns(columns) {
			this.selectedColumns = columns;
		},

		switchBranding() {
			const currentBranding = this.showBranding;
			this.showBranding = !currentBranding;

			// this.filterKeywords();
			this.filterKeywordsV2();
		},

		setBranding(value) {
			this.showBranding = value;
			// this.filterKeywords();
			this.filterKeywordsV2();
		},

		setFilterByTop(value) {
			this.filterByTop = value;
			// this.filterKeywords();
			this.filterKeywordsV2();
		},

		setFilterString(value) {
			try {
				this.filterString = value;
				// this.filterKeywords();
				this.filterKeywordsV2();
			} catch (error) {
				console.error('Error in setFilterString:', error);
				throw error;
			}
		},

		filterKeywords() {
			const { value: search, type: searchType } = this.filterString;
			const top = this.filterByTop;
			const showBranding = this.showBranding;

			const keywordFilter = keyword => keyword?.keyword?.toLowerCase() === search.toLowerCase();
			const urlFilter = keyword => keyword?.client_full_url?.toLowerCase() === search.toLowerCase();
			const labelFilter = keyword => keyword?.labels?.some(label => label?.name?.toLowerCase() === search.toLowerCase());

			const globalFilter = keyword => {
				const keywordSearch = keyword?.keyword?.toLowerCase().includes(search.toLowerCase());
				const urlSearch = keyword?.client_full_url?.toLowerCase().includes(search.toLowerCase());
				const labelSearch = keyword?.labels?.some(label => label?.name?.toLowerCase().includes(search.toLowerCase()));

				return keywordSearch || urlSearch || labelSearch;
			};

			let filterFunction;
			switch (searchType) {
				case 'keyword':
					filterFunction = keywordFilter;
					break;
				case 'url':
					filterFunction = urlFilter;
					break;
				case 'label':
					filterFunction = labelFilter;
					break;
				case 'global':
					filterFunction = globalFilter;
					break;
				default:
					filterFunction = () => true;
			}

			let filteredKeywords = [...this.raw_keywords];

			if (!showBranding) {
				filteredKeywords = filteredKeywords.filter(keyword => {
					const hasBranding = keyword.labels?.some(label => label.name === 'Branding') ?? false;
					return !hasBranding;
				});
			}

			if (search !== '') {
				filteredKeywords = filteredKeywords.filter(keyword => filterFunction(keyword));
			}

			if (top !== 0) {
				filteredKeywords = filteredKeywords.filter(keyword =>
					keyword.client.position <= top &&
					keyword.client.position !== null &&
					keyword.client.position !== 101
				);
			}

			const keywordMap = new Map();

			filteredKeywords.forEach(keyword => {
				const keywordText = keyword.keyword.toLowerCase();

				if (keywordMap.has(keywordText)) {
					const existingKeyword = keywordMap.get(keywordText);
					if (keyword.client.position < existingKeyword.client.position) {
						keywordMap.set(keywordText, keyword);
					}
				} else {
					keywordMap.set(keywordText, keyword);
				}
			});

			const dedupedKeywords = Array.from(keywordMap.values());

			this.keywords = dedupedKeywords;
			this.count_keywords = this.keywords.length;
		},

		filterKeywordsV2() {
			if (!this.filterString || typeof this.filterString !== 'object') {
				console.error('filterString is not an object:', this.filterString);
				return;
			}

			const { value: search, type: searchType, id: searchId } = this.filterString;
			const top = this.filterByTop;
			const showBranding = this.showBranding;

			const searchLower = typeof search === 'string' ? search.toLowerCase() : '';

			const matchesLabel = (label) => {
				if (!label) return false;

				const labelId = label.id ?? label.label_id;
				const labelName = (label.label ?? label.name ?? '').toLowerCase();

				// Prefer filtering by id when we have one (more robust than text)
				if (searchId != null && labelId != null && String(labelId) === String(searchId)) {
					return true;
				}

				// Fallback to name-based match (for free-text label searches)
				if (searchLower && labelName) {
					return labelName === searchLower;
				}

				return false;
			};

			const keywordFilter = keyword =>
				keyword?.keyword?.toLowerCase() === searchLower;

			const urlFilter = keyword =>
				keyword?.client_full_url?.toLowerCase() === searchLower;

			const labelFilter = keyword =>
				keyword?.labels?.some(matchesLabel);

			const globalFilter = keyword => {
				const keywordSearch = keyword?.keyword?.toLowerCase().includes(searchLower);
				const urlSearch = keyword?.client_full_url?.toLowerCase().includes(searchLower);
				const labelSearch = keyword?.labels?.some(label => {
					if (!label) return false;

					const labelId = label.id ?? label.label_id;
					const labelName = (label.label ?? label.name ?? '').toLowerCase();

					const matchesId = searchId != null && labelId != null && String(labelId) === String(searchId);
					const matchesText = searchLower && labelName.includes(searchLower);

					return matchesId || matchesText;
				});

				return keywordSearch || urlSearch || labelSearch;
			};

			let filterFunction;
			switch (searchType) {
				case 'keyword':
					filterFunction = keywordFilter;
					break;
				case 'url':
					filterFunction = urlFilter;
					break;
				case 'label':
					filterFunction = labelFilter;
					break;
				case 'global':
					filterFunction = globalFilter;
					break;
				default:
					filterFunction = () => true;
			}

			let filteredKeywords = [...this.raw_keywords_v2];

			if (!showBranding) {
				filteredKeywords = filteredKeywords.filter(keyword => {
					const hasBranding = keyword.labels?.some(label => (label.label ?? label.name) === 'Branding') ?? false;
					return !hasBranding;
				});
			}

			if (searchLower !== '') {
				filteredKeywords = filteredKeywords.filter(keyword => filterFunction(keyword));
			}

			if (top !== 0) {
				filteredKeywords = filteredKeywords.filter(keyword =>
					keyword.client_position <= top &&
					keyword.client_position !== null &&
					keyword.client_position !== 101
				);
			}

			const keywordMap = new Map();

			filteredKeywords.forEach(keyword => {
				const keywordText = keyword.keyword.toLowerCase();

				if (keywordMap.has(keywordText)) {
					const existingKeyword = keywordMap.get(keywordText);
					if (keyword.client_position < existingKeyword.client_position) {
						keywordMap.set(keywordText, keyword);
					}
				} else {
					keywordMap.set(keywordText, keyword);
				}
			});

			const dedupedKeywords = Array.from(keywordMap.values());

			this.keywords_v2 = dedupedKeywords;
		},

		resetFilter() {
			this.filterString = { value: '', type: 'global', id: null };
			this.filterByTop = 0;

			this.keywords_v2 = [...this.raw_keywords_v2];
		},

		setLoading(value) {
			this.loading = value;
		},

		async deleteKeywords(marketId, keywords_id) {
			const response = await axios.post(
				'vuejs/delete_keywords',
				{ keywords: keywords_id, market_id: marketId },
				{ withCredentials: true },
			);
			if (response.data.success === 'success') {
				this.fetchDataV2(marketId);
				useNotificationStore().open('success', 'Keywords deleted successfully');
			} else {
				useNotificationStore().open('error', 'Failed to delete keywords');
			}
		},

		async editLabels(marketId, editedLabels) {
			const response = await axios.post(
				'vuejs/update_label',
				{ market_id: marketId, labels: editedLabels },
				{ withCredentials: true },
			);
			if (response.data.success === 'success') {
				useNotificationStore().open('success', 'Labels updated successfully');
				await this.fetchLabels(marketId);
				await this.fetchDataV2(marketId);
				this.updateKeywordsLabelReferencesFromStore(editedLabels);
			}
			else {
				useNotificationStore().open('error', 'Failed to update labels');
			}
		},

		updateKeywordsLabelReferencesFromStore(editedLabels) {
			const editedLabelMap = new Map();
			editedLabels.forEach(editedLabel => {
				editedLabelMap.set(editedLabel.id, editedLabel);
				editedLabelMap.set(String(editedLabel.id), editedLabel);
			});

			const storeLabelMap = new Map();
			this.labels.forEach(label => {
				storeLabelMap.set(label.id, label);
				storeLabelMap.set(String(label.id), label);
			});

			const updateKeywordsArray = (keywordsArray) => {
				return keywordsArray.map(keyword => {
					if (!keyword.labels || !Array.isArray(keyword.labels)) {
						return keyword;
					}

					const needsUpdate = keyword.labels.some(label => {
						const labelId = label.label_id || label.id;
						return labelId && (editedLabelMap.has(labelId) || editedLabelMap.has(String(labelId)));
					});

					if (!needsUpdate) {
						return keyword;
					}

					const updatedLabels = keyword.labels.map(label => {
						const labelId = label.label_id || label.id;
						const labelIdStr = labelId ? String(labelId) : null;
						
						if (labelId && (editedLabelMap.has(labelId) || editedLabelMap.has(labelIdStr))) {
							const updatedLabel = storeLabelMap.get(labelId) || storeLabelMap.get(labelIdStr) || editedLabelMap.get(labelId) || editedLabelMap.get(labelIdStr);
							
							if (!updatedLabel) {
								return label;
							}
							
							return {
								...label,
								label: updatedLabel.label,
								color: updatedLabel.color,
								...(label.name !== undefined && { name: updatedLabel.label }),
								...(label.colors !== undefined && { colors: updatedLabel.color }),
							};
						}
						return label;
					});

					return {
						...keyword,
						labels: updatedLabels,
					};
				});
			};

			this.keywords = updateKeywordsArray(this.keywords);
			this.keywords_v2 = updateKeywordsArray(this.keywords_v2);
			this.raw_keywords_v2 = updateKeywordsArray(this.raw_keywords_v2);
		},

		async assignLabels(add_labels, remove_labels) {
			const response = await axios.post(
				'vuejs/assign_labels',
				{ add_labels: add_labels, remove_labels: remove_labels },
				{ withCredentials: true },
			);
			if (response.data.success === 'success') {
				this.updateKeywordsLabels(add_labels, remove_labels);

				useNotificationStore().open('success', 'Labels assigned successfully');
			} else {
				useNotificationStore().open('error', 'Failed to assign labels');
			}
		},

		updateKeywordsLabels(add_labels, remove_labels) {

			add_labels.forEach(add => {
				add.keywords.forEach(keywordId => {
					const keyword = this.keywords.find(k => k.id == keywordId);

					if (keyword) {
						const label = this.labels.find(l => l.id == add.label_id);
						if (label && !keyword.labels.some(l => l.id == label.id)) {
							const mappedLabel = { ...label, colors: label.color, name: label.label };
							delete mappedLabel.description;
							delete mappedLabel.color;
							delete mappedLabel.label;

							keyword.labels.push(mappedLabel);
						}
					}
				});
			});

			remove_labels.forEach(remove => {
				remove.keywords.forEach(keywordId => {
					const keyword = this.keywords.find(k => k.id == keywordId);
					if (keyword) {
						keyword.labels = keyword.labels.filter(l => l.id != remove.label_id);
					}
				});
			});

			this.keywords = [...this.keywords];
		},

		async importKeywords(marketId, keywords) {
			const response = await axios.post(
				'vuejs/import_keywords',
				{
					market_id: marketId,
					keywords: keywords,
				},
				{ withCredentials: true },
			);

			return response.data;
		},

		async createLabel(marketId, labelColor, labelName, labelDescription, labelBooleans = {}) {
			const payload = {
				market_id: marketId,
				label_color: labelColor,
				label_name: labelName,
				label_description: labelDescription,
			};
			if (labelBooleans && Object.keys(labelBooleans).length > 0) {
				Object.assign(payload, labelBooleans);
			}
			const response = await axios.post(
				'vuejs/create_label',
				payload,
				{ withCredentials: true },
			);
			if (response.data.success === 'success') {
				this.fetchLabels(marketId);
				useNotificationStore().open('success', 'Label created successfully');
			} else {
				useNotificationStore().open('error', 'Failed to create label');
			}
		},

		async import_keywords(marketId, keywords) {
			await axios
				.post(
					'vuejs/import_keywords',
					{
						market_id: marketId,
						data: keywords,
					},
					{ withCredentials: true },
				)
				.then(response => {
					sessionStorage.setItem('kwImport', 'done');
					// window.location.reload();
					this.setReport(response);
				});
		},

		_averagePosition(keywords) {
			if (keywords.length === 0) return 0;
			const totalPosition = keywords.reduce((acc, keyword) => acc + keyword.client_position, 0);
			const average = totalPosition / keywords.length;
			return parseFloat(average.toFixed(2));
		},
		_sumVolume(keywords) {
			return keywords.reduce((acc, keyword) => acc + keyword.volume, 0);
		},

		async bulk_keywords(marketId, keywords, append_labels) {
			const response = await axios.post(
				'vuejs/bulk_keywords',
				{
					'market_id': marketId,
					'append_labels': append_labels,
					'data': keywords,
				},
				{ withCredentials: true },
			);

			this.setReport(response);
		},

		setReport(response) {
			this.num_submitted = response.data.num_submitted;
			this.num_unique = response.data.num_unique;
			this.num_existent = response.data.num_existent;
			this.num_duplicates = response.data.num_duplicates;
			this.duplicates = response.data.duplicates;
			this.existent_keywords_data = response.data.existent_keywords;
			this.existent_keywords = response.data.existent_keywords.length > 0 ? response.data.existent_keywords.join(', ') : '--';
			this.existent_labels = response.data.existent_labels;
			this.num_added = response.data.num_added;
			this.num_updated = response.data.num_updated;
			this.added_keywords = response.data.added_keywords;
			this.updated_keywords = response.data.updated_keywords;
		},

		async deleteLabel(labelsId, marketId) {
			const response = await axios.post(
				'vuejs/delete_label',
				{
					labels_id: labelsId,
				},
				{ withCredentials: true },
			);

			this.fetchLabels(marketId);

			if (response.data.success === 'success') {
				return true;
			}

			return false;
		},
		async restoreLabel(labelsId) {
			const response = await axios.post(
				'vuejs/restore_label',
				{
					labels_id: labelsId,
				},
				{ withCredentials: true },
			);

			if (response.data.success === 'success') {
				return true;
			}

			return false;
		},
		async getNewKeywordsVolume(marketId) {
			const response = await axios.get(`https://global-api-kcvp2mazlq-ew.a.run.app/new_keywords_volume_cmp/${marketId}`);
			return response.data;
		},
		async getNewKeywordsCompetitors(marketId) {
			const response = await axios.get(`https://global-api-kcvp2mazlq-ew.a.run.app/new_keywords_update_cmp_potential/${marketId}`);
			return response.data;
		},
		async getLabelTrend(marketId, labelId) {
			const response = await axios.post(
				'vuejs/get_label_trend',
				{
					market_id: marketId,
					label_id: labelId,
				},
				{ withCredentials: true },
			);

			if (response.data.success === 'success') {
				this.label_trend = response.data.data;
			}

			return false;
		},
		async getKeywordTrend(keywordId) {
			const response = await axios.post(
				'vuejs/keyword',
				{
					keyword_id: keywordId
				},
				{ withCredentials: true },
			);

			if (response.data.keyword_historic_data.length > 0) {
				this.keyword_trend = response.data.keyword_historic_data;
			}

			return false;
		},
		async fetchCompetitors(marketId, forceRefresh = false) {
			// Security check: prevent execution if marketId is null, undefined, or empty
			if (!marketId) {
				this.raw_competitors_list = [];
				this.competitors_list = [];
				this.selectedCompetitorsLabel = null;
				this.lastFetchedMarketId = null;
				this.fetchCompetitorsPromise = null;
				return [];
			}

			// Cache check: if we already have data for this marketId, return cached data
			if (!forceRefresh && this.lastFetchedMarketId === marketId && this.competitors_list.length > 0) {
				console.log('fetchCompetitors: Using cached data for marketId:', marketId);
				return this.competitors_list;
			}

			// Request deduplication: if a request for this marketId is already pending, reuse the promise
			if (!forceRefresh && this.fetchCompetitorsPromise && this.lastFetchedMarketId === marketId) {
				console.log('fetchCompetitors: Reusing pending request for marketId:', marketId);
				try {
					return await this.fetchCompetitorsPromise;
				} catch (error) {
					// If the pending request failed, allow a retry
					this.fetchCompetitorsPromise = null;
					this.lastFetchedMarketId = null;
					throw error;
				}
			}

			// Create and store the promise for this request
			this.lastFetchedMarketId = marketId;
			this.fetchCompetitorsPromise = (async () => {
				try {
					this.raw_competitors_list = [];
					this.competitors_list = [];

					const response = await axios.post('vuejs/get_competitor_list', {
						market_id: marketId,
					}, { withCredentials: true });

					this.raw_competitors_list = response.data.data;
					this.competitors_list = response.data.data;
					this.selectedCompetitorsLabel = null;

					return response.data.data;
				} catch (error) {
					// Reset on error so we can retry
					this.lastFetchedMarketId = null;
					throw error;
				} finally {
					// Clear the promise after completion (success or failure)
					this.fetchCompetitorsPromise = null;
				}
			})();

			return await this.fetchCompetitorsPromise;
		},
		resetTableFilters() {
			this.tableFilters = {
				global: { value: null, matchMode: 'contains' },
				keyword: { operator: 'AND', constraints: [{ value: null, matchMode: 'contains' }] },
				labels: { value: null, matchMode: 'containsAny' },
				avg_words: { operator: 'AND', constraints: [{ value: null, matchMode: 'equals' }] },
				'client.position': { value: [0, 101], matchMode: 'between' },
				seo_potential: { value: [0, 101], matchMode: 'between' },
				volume: { operator: 'AND', constraints: [{ value: null, matchMode: 'equals' }] },
				client_full_url: { operator: 'AND', constraints: [{ value: null, matchMode: 'contains' }] },
			};
		},
		updateSortPreferences(newPreferences) {
			this.sortTablePreferences = newPreferences;
		},
		async getMaxKeywords(marketId, businessAccountId) {
			const response = await axios.post(
				'vuejs/get_max_keywords',
				{
					market_id: marketId,
					business_account_id: businessAccountId
				},
				{ withCredentials: true },
			);

			this.totalKeywords = response.data.total_keywords;
			this.maxKeywords = response.data.max_keywords;
		},
		async linkGeoKeywords(marketId, question, keywordIds) {
			try {
				const response = await axios.post(
					'vuejs/link_geo_keywords',
					{
						market_id: marketId,
						question: question,
						keyword_ids: keywordIds,
					},
					{ withCredentials: true },
				);

				if (response.data.success === 'success') {
					useNotificationStore().open('success', 'Question linked to keywords successfully');
					return response.data;
				} else {
					useNotificationStore().open('error', 'Failed to link question to keywords');
					return null;
				}
			} catch (error) {
				const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
				console.error('An error occurred:', errorMessage);
				useNotificationStore().open('error', `Error: ${errorMessage}`);
				return null;
			}
		},
		async updateSelectedCompetitor(competitorId, marketId = null) {
			if (competitorId === null) {
				this.selectedCompetitorsLabel = null;
			}
			else {
				this.selectedCompetitorsLabel = competitorId;
			}

			const market = marketId || this.selectedMarket;
			if (market) {
				this.loading = true;
				try {
					await Promise.all([
						this.fetchDataV2(market, false, competitorId),
						this.fetchAveragePositionRange(market, competitorId)
					]);
				}
				catch (error) {
					console.error('Error fetching data:', error);
					this.loading = false;
				}
				finally {
					this.loading = false;
				}
			}
		},
		async getAllKeywordsOnUrl(value, marketId, type = 'url') {
			const response = await axios.post('vuejs/keywords_from_url', {
				...(type === 'url' ? { url: value } : { keyword_id: value }),
				market_id: marketId,
			}, { withCredentials: true })
				.then(response => {
					return response.data.data;
				})
				.catch(error => {
					console.error('Error fetching keywords from URL:', error);
					return null;
				});
			return response;
		},
		async assignKeywordToUrl(url, keywordId, marketId) {

			const response = await axios.post('vuejs/assign_keyword_to_url', {
				url: url,
				keyword_id: keywordId,
				market_id: marketId,
			}, { withCredentials: true })
				.then(response => {
					useUrlsStore().fetchData(marketId);
				});
		},
		async deleteKeywordAssignment(url, marketId) {

			const response = await axios.post('vuejs/delete_keyword_assignment', {
				url: url,
				market_id: marketId,
			},
				{ withCredentials: true })
				.then(response => {
					useUrlsStore().fetchData(marketId);
				});
		}
	},
	persist: true,
});
